import apiClient from './api-client';
import {
  ImportOptions,
  ImportResult,
  ValidationResult,
  FileValidationOptions,
} from '@/types/data-import.types';

const DEFAULT_MAX_SIZE_MB = 10;
const ALLOWED_FILE_TYPES = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'application/csv',
];

export const dataImportService = {
  /**
   * Import loans from a file
   */
  async importLoans(
    file: File,
    options?: ImportOptions
  ): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    if (options?.mappings) {
      formData.append('mappings', JSON.stringify(options.mappings));
    }

    const response = await apiClient.post(
      '/api/v1/data-import/loans',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (options?.onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            options.onProgress(percentCompleted);
          }
        },
      }
    );

    return response.data;
  },

  /**
   * Import benchmark rates from a file
   */
  async importBenchmarkRates(
    file: File,
    options?: ImportOptions
  ): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    if (options?.mappings) {
      formData.append('mappings', JSON.stringify(options.mappings));
    }

    const response = await apiClient.post(
      '/api/v1/data-import/benchmark-rates',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (options?.onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            options.onProgress(percentCompleted);
          }
        },
      }
    );

    return response.data;
  },

  /**
   * Validate file before upload
   */
  async validateFile(
    file: File,
    options?: FileValidationOptions
  ): Promise<ValidationResult> {
    const maxSizeMB = options?.maxSizeMB || DEFAULT_MAX_SIZE_MB;
    const allowedTypes = options?.allowedTypes || ALLOWED_FILE_TYPES;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    const errors: string[] = [];

    // Check file size
    if (file.size > maxSizeBytes) {
      errors.push(`File size exceeds ${maxSizeMB}MB limit`);
    }

    // Check file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const isValidType =
      allowedTypes.includes(file.type) ||
      (fileExtension && ['csv', 'xlsx', 'xls'].includes(fileExtension));

    if (!isValidType) {
      errors.push(
        'File type not supported. Please upload Excel (.xlsx, .xls) or CSV files.'
      );
    }

    // Check if file is empty
    if (file.size === 0) {
      errors.push('File is empty');
    }

    return {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
    };
  },

  /**
   * Get accepted file types for input element
   */
  getAcceptedFileTypes(): string {
    return '.csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  },
};
